/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { Play, RotateCcw, Home } from 'lucide-react';

interface PauseOverlayProps {
  onResume: () => void;
  onRestart: () => void;
  onExit: () => void;
  score: number;
  level: number;
  kills: number;
}

export const PauseOverlay: React.FC<PauseOverlayProps> = ({
  onResume,
  onRestart,
  onExit,
  score,
  level,
  kills,
}) => {
  return (
    <div id="pause-overlay" className="absolute inset-0 bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-6 z-25">
      <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-2xl text-slate-200">
        
        {/* Header indicator */}
        <div className="text-center space-y-1">
          <span className="text-cyan-400 font-mono text-[10px] tracking-widest uppercase">
            // FLIGHT PAUSED //
          </span>
          <h2 className="text-3xl font-black text-white tracking-tight">
            TACTICAL SCREEN
          </h2>
          <p className="text-xs text-slate-400 font-mono">
            COCKPIT SYSTEMS WAITING FOR TRANSMISSION
          </p>
        </div>

        {/* Current Flight Mission Stats */}
        <div className="grid grid-cols-3 gap-3 my-6 text-center font-mono bg-slate-950/60 p-3.5 border border-slate-800/60 rounded-xl">
          <div>
            <div className="text-xs text-slate-400">SCORE</div>
            <div className="text-lg font-bold text-white">{score.toLocaleString()}</div>
          </div>
          <div>
            <div className="text-xs text-slate-400">LEVEL</div>
            <div className="text-lg font-bold text-cyan-400">{level}</div>
          </div>
          <div>
            <div className="text-xs text-slate-400">KILLS</div>
            <div className="text-lg font-bold text-rose-400">{kills}</div>
          </div>
        </div>

        {/* Action Buttons list */}
        <div className="space-y-3">
          <button
            id="pause-resume-btn"
            onClick={onResume}
            className="w-full py-3 rounded-xl font-bold bg-cyan-500 text-slate-950 hover:bg-cyan-400 transition transform hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-2 cursor-pointer text-sm"
          >
            <Play className="w-4 h-4 fill-slate-950" /> RESUME MISSION (ESC)
          </button>

          <button
            id="pause-restart-btn"
            onClick={onRestart}
            className="w-full py-3 rounded-xl font-bold bg-slate-800 text-slate-200 border border-slate-700 hover:bg-slate-700 transition flex items-center justify-center gap-2 cursor-pointer text-sm"
          >
            <RotateCcw className="w-4 h-4" /> RESTART EXERCISE
          </button>

          <button
            id="pause-exit-btn"
            onClick={onExit}
            className="w-full py-2.5 rounded-xl font-medium bg-slate-950 text-slate-400 hover:text-slate-100 hover:bg-slate-900 border border-slate-800/80 transition flex items-center justify-center gap-2 cursor-pointer text-xs"
          >
            <Home className="w-4 h-4" /> ABORT TO BASE MENU
          </button>
        </div>

        {/* Key map cheatsheet footer */}
        <div className="mt-6 pt-4 border-t border-slate-800 text-[11px] font-mono text-slate-500 text-center uppercase tracking-wide">
          Tip: Hit <kbd className="px-1 text-[10px] bg-slate-800 border border-slate-700 rounded text-slate-300">Space</kbd> during main flight to discharge heavy seekers!
        </div>

      </div>
    </div>
  );
};
